import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import LoadingButton from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FOOTBALL_REWARD_BOX_ID } from '@sorare/core/src/constants/__generated__/routes';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { glossary } from '@sorare/core/src/lib/glossary';

import {
  ClaimObjectiveInlineButton_managerProgressionTask,
  ClaimWarmupObjectiveMutation,
  ClaimWarmupObjectiveMutationVariables,
} from './__generated__/index.graphql';

export const CLAIM_WARMUP_OBJECTIVE_MUTATION = gql`
  mutation ClaimWarmupObjectiveMutation($name: ManagerProgressionTaskSlug!) {
    claimManagerProgressionTask(input: { name: $name, sport: FOOTBALL }) {
      managerProgressionTaskOrMission {
        id
        aasmState
        probabilisticBundles {
          id
        }
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  ClaimWarmupObjectiveMutation,
  ClaimWarmupObjectiveMutationVariables
>;

export const ClaimObjectiveInlineButton = ({
  managerProgressionTask,
}: {
  managerProgressionTask: ClaimObjectiveInlineButton_managerProgressionTask;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [claim, { loading }] = useMutation(CLAIM_WARMUP_OBJECTIVE_MUTATION, {
    showErrorsWithSnackNotification: true,
  });

  if (
    managerProgressionTask.aasmState !==
      ManagerProgressionTaskState.COMPLETED &&
    managerProgressionTask.aasmState !== ManagerProgressionTaskState.CLAIMED
  ) {
    return null;
  }
  const claimable =
    managerProgressionTask.aasmState === ManagerProgressionTaskState.COMPLETED;
  return (
    <LoadingButton
      size="compact"
      color="secondary"
      loading={loading}
      disabled={!claimable}
      onClick={async () => {
        const { data } = await claim({
          variables: {
            name: managerProgressionTask.name,
          },
        });
        const bundleId =
          data?.claimManagerProgressionTask?.managerProgressionTaskOrMission?.probabilisticBundles.at(
            0
          )?.id;
        if (data?.claimManagerProgressionTask?.errors || !bundleId) {
          return;
        }
        navigate(
          `${generatePath(FOOTBALL_REWARD_BOX_ID, {
            id: bundleId,
          })}?redirectUrl=${`${location.pathname}?initialActiveIndex=4`}`
        );
      }}
    >
      <FormattedMessage {...(claimable ? glossary.claim : glossary.claimed)} />
    </LoadingButton>
  );
};

ClaimObjectiveInlineButton.fragments = {
  managerProgressionTask: gql`
    fragment ClaimObjectiveInlineButton_managerProgressionTask on ManagerProgressionTask {
      id
      name
      aasmState
    }
  ` as TypedDocumentNode<ClaimObjectiveInlineButton_managerProgressionTask>,
};
